import { leadStateText } from "@/lib/campaign";
import type {
  CadenceBody, CadencePhase, LeadCadenceDecision, LeadCadenceMembership,
} from "@/lib/types";
import { formatDateTime } from "./utils.js";

/**
 * A cadence as the console shows and edits it (ADR-066): the words for why a lead is due when it
 * is and why its priority is what it is, the buckets of a campaign's work, and the arithmetic
 * between the editor's form and the JSON body core-api validates (`services/shared/cadence_body.py`
 * in `platform`). The server decides every bound; the copies here keep a value it would refuse off
 * the form and say why before a round trip does. Pure, for `test/cadence.test.ts`.
 */

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "outline";

export interface Term {
  label: string;
  description: string;
}

// --- strategies -------------------------------------------------------------------------------------

export const DID_STRATEGIES = ["branded", "same_as_last", "unbranded", "rotate"] as const;

const STRATEGY: Record<string, Term> = {
  branded: { label: "Branded number", description: "The campaign's own caller ID." },
  same_as_last: { label: "Same as the last call", description: "The number the lead was last called from, so the callback reaches the same place." },
  unbranded: { label: "Local number", description: "Another number routed to this campaign: one in the lead's state when there is one, else any of the others, else the branded number (ADR-069)." },
  rotate: { label: "Rotating numbers", description: "The campaign's numbers in turn: the one used longest ago, never the number the lead was last called from while there is another (ADR-075)." },
};

export function strategyText(strategy: string): Term {
  return STRATEGY[strategy] ?? { label: strategy, description: "Not a strategy this console knows." };
}

/** What this build runs: every strategy since rotation runs (ADR-075). */
export const SUPPORTED_STRATEGIES: readonly string[] = ["branded", "same_as_last", "unbranded", "rotate"];

// --- why a lead is where it is --------------------------------------------------------------------

const NEXT_REASON: Record<string, string> = {
  enrolled: "Just enrolled",
  initial_delay: "Waiting out the initial delay",
  adopted: "Placed at the position its calls imply",
  between_calls: "The next call of the cycle",
  after_cycle: "Waiting for the next cycle",
  after_conversation: "Spoke with a rep, waiting for the next cycle",
  disposition_wait: "The outcome's own wait",
  technical_retry: "Retrying after a technical failure",
  invalid_retry: "Retrying a number the carrier refused",
  not_worked: "Hung up while connecting, not charged",
  not_counted: "A call that did not count",
  callback: "A callback the rep promised",
  hold: "Held",
  skipped: "Skipped by a rep",
  expired_replanned: "Replanned to the next cycle",
  reenrolled: "Re-enrolled from the top",
  quiet_hours: "Outside the calling hours",
  on_a_call: "On a call",
  daily_cap: "Called enough for today",
  needs_disposition: "Waiting for the rep's outcome",
  total_attempts: "Out of attempts",
  expired: "The cadence has run its course",
  cadence_end: "Past the last phase",
  invalid_number: "The number is not in service",
  dnc: "Do not call",
  exit_stage: "Left the lifecycle",
  terminal: "Finished by the outcome",
  handed_over: "Handed to another campaign",
  no_consent: "No consent evidence",
  no_timezone: "No time zone can be known",
  retired: "Past the list horizon",
};

export function nextReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return NEXT_REASON[reason] ?? reason.replaceAll("_", " ");
}

const PRIORITY_REASON: Record<string, string> = {
  callback: "A promised callback",
  about_to_sign: "Near signing",
  mid_cycle: "The second call of a double dial",
  initial_fresh: "Never reached yet",
};

/** `initial_d3` is the phase's band on day 3; `followup` the phase's flat band. */
export function priorityReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (PRIORITY_REASON[reason]) return PRIORITY_REASON[reason];
  const aged = /^(.+)_d(\d+)$/.exec(reason);
  if (aged) return `Day ${aged[2]} of the ${(aged[1] ?? "").replaceAll("_", " ")} phase`;
  return `The ${reason.replaceAll("_", " ")} phase`;
}

export function positionText(cycle: number, step: number): string {
  return `Cycle ${cycle}, call ${step + 1} next`;
}

// --- the lead's place, as the record's Follow-up card shows it -----------------------------------

const HOLD: Record<string, string> = {
  "cadence:paused": "Paused by an admin",
  "firm:paused": "The firm is paused",
  appointment: "An appointment is booked",
  appointment_set: "An appointment is booked",
  contract_review: "A contract review is booked",
  hold: "A hold outcome",
};

/** Why a hold keeps the lead off the list, in words; a reason this console does not know reads as
 *  itself. */
export function holdText(heldBy: string): string {
  if (HOLD[heldBy]) return HOLD[heldBy];
  const words = heldBy.replaceAll(":", " ").replaceAll("_", " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : "Held";
}

export type StatusKind = "due" | "overdue" | "held" | "on_call" | "needs_outcome" | "rest";

/** The headline of a lead's place on a cadence campaign: what the dialer will do with it next. */
export interface CadenceStatus {
  kind: StatusKind;
  tone: Tone;
  /** "Due in 3 h 58 min", "Held until …", "Finished by the outcome". */
  headline: string;
  /** The reason under it. */
  detail: string | null;
  /** The instant the headline speaks of, for a tooltip; null when there is none. */
  at: string | null;
}

/** Due for longer than this with nobody taking it is overdue: the campaign page's bucket. */
export const OVERDUE_AFTER_S = 15 * 60;

const AT_REST: ReadonlySet<string> = new Set(["done", "dnc", "exhausted", "retired", "blocked"]);
const REST_TONE: Record<string, Tone> = {
  done: "success", dnc: "danger", blocked: "danger", exhausted: "neutral", retired: "neutral",
};

export function cadenceStatus(m: LeadCadenceMembership, now: number = Date.now()): CadenceStatus {
  if (m.held_by) {
    return {
      kind: "held",
      tone: "neutral",
      headline: m.next_at ? `Held until ${formatDateTime(m.next_at)}` : "Held",
      detail: holdText(m.held_by),
      at: m.next_at,
    };
  }
  if (AT_REST.has(m.state)) {
    const state = leadStateText(m.state);
    return {
      kind: "rest",
      tone: REST_TONE[m.state] ?? "neutral",
      headline: nextReasonText(m.next_reason) ?? state.label,
      detail: state.description,
      at: null,
    };
  }
  if (m.state === "offered" || m.state === "in_flight") {
    return {
      kind: "on_call",
      tone: "accent",
      headline: "On a call",
      detail: m.assigned_rep ? `With ${m.assigned_rep}` : leadStateText(m.state).description,
      at: null,
    };
  }
  if (m.state === "needs_disposition") {
    return {
      kind: "needs_outcome",
      tone: "warning",
      headline: "Waiting for the rep's outcome",
      detail: "The call ended without an outcome. The cadence moves on once a rep records one.",
      at: null,
    };
  }
  const why = nextReasonText(m.next_reason);
  const at = m.next_attempt_at;
  const delta = at ? Math.round((Date.parse(at) - now) / 1000) : 0;
  if (!at || Math.abs(delta) < 45) return { kind: "due", tone: "accent", headline: "Due now", detail: why, at };
  if (delta > 0 || -delta <= OVERDUE_AFTER_S) {
    return { kind: "due", tone: "accent", headline: `Due ${relativeSpan(at, now)}`, detail: why, at };
  }
  return { kind: "overdue", tone: "warning", headline: `Due ${relativeSpan(at, now)}`, detail: why, at };
}

/** A state the dialer never calls again until a person or a list recycle moves it. */
export function atRest(state: string): boolean {
  return AT_REST.has(state);
}

/** Where in the cycle, said for the tile: what is next while the row still dials, what was made
 *  once it rests. */
export function cycleWords(m: LeadCadenceMembership): string {
  if (atRest(m.state)) {
    if (m.step === 0) return "no call made";
    return `${m.step} call${m.step === 1 ? "" : "s"} made`;
  }
  return `call ${m.step + 1} next`;
}

/** "day 3" from an aged phase band; null when the priority says something else. */
export function phaseDay(priorityReason: string | null | undefined): string | null {
  const aged = /^(.+)_d(\d+)$/.exec(priorityReason ?? "");
  return aged ? `day ${aged[2]}` : null;
}

const DECISION: Record<string, string> = {
  claimed: "Claimed for a call",
  enrolled: "Enrolled",
  adopted: "Adopted",
  after_call: "After the call",
  after_disposition: "After the outcome",
  recheck: "Rechecked",
  reaped: "Taken back by the dialer",
  late_outcome: "A late outcome",
  reenrolled: "Started again from the top",
  reanchored: "Re-anchored",
  reopened: "Reopened: carries on where it stopped",
  blocked: "Blocked",
  released: "Released",
  shadow: "Shadow decision",
  malformed: "Cadence not readable",
  sms_step: "Text message step",
};

const DECISION_TONE: Record<string, Tone> = {
  claimed: "accent", enrolled: "accent", adopted: "accent", reenrolled: "accent", reanchored: "accent",
  reopened: "accent",
  reaped: "warning", released: "warning", blocked: "danger", malformed: "danger", shadow: "outline",
};

export interface DecisionWords {
  title: string;
  /** The reason, the state it left the row in, and how long until the next call. */
  detail: string | null;
  tone: Tone;
}

/** One decision of the dialer's, in words a rep reads: what it did, why, and what it left. */
export function decisionText(d: LeadCadenceDecision): DecisionWords {
  const title = DECISION[d.kind] ?? (d.kind ? d.kind[0]!.toUpperCase() + d.kind.slice(1).replaceAll("_", " ") : "Decision");
  const parts: string[] = [];
  if (d.reason && d.reason !== d.kind) {
    const known = NEXT_REASON[d.reason];
    parts.push(known ?? d.reason[0]!.toUpperCase() + d.reason.slice(1).replaceAll("_", " "));
  } else if (d.kind === "adopted") {
    parts.push(NEXT_REASON.adopted!);
  }
  if (d.to_state && d.to_state !== d.from_state) parts.push(`now ${leadStateText(d.to_state).label.toLowerCase()}`);
  if (d.next_attempt_at) {
    const wait = Math.round((Date.parse(d.next_attempt_at) - Date.parse(d.at)) / 1000);
    parts.push(wait <= 0 ? "next call right away" : `next call in ${spanText(wait)}`);
  }
  let tone: Tone = DECISION_TONE[d.kind] ?? "neutral";
  if (d.to_state === "done") tone = "success";
  else if (d.to_state === "dnc" || d.to_state === "blocked") tone = "danger";
  return { title, detail: parts.join(" · ") || null, tone };
}

const REQUEST: Record<string, string> = {
  pause: "Pause",
  resume: "Resume",
  reenroll: "Start again from the top",
  reanchor: "Re-anchor",
  reopen: "Back on the list after a reopen",
};

/** What an admin asked the dialer for, by the request's kind. */
export function requestText(kind: string): string {
  return REQUEST[kind] ?? kind.replaceAll("_", " ");
}

// --- the buckets ------------------------------------------------------------------------------------

export const BUCKETS = [
  "due", "overdue", "held", "technical", "blocked", "needs_disposition", "exhausted", "double_membership",
] as const;
export type Bucket = (typeof BUCKETS)[number];

const BUCKET: Record<Bucket, Term & { tone: Tone }> = {
  due: { label: "Due", description: "Dialable now, waiting for a free rep.", tone: "accent" },
  overdue: { label: "Overdue", description: "Due for more than fifteen minutes with nobody taking it.", tone: "warning" },
  held: { label: "Held", description: "A hold keeps it: a pause, a booking, a hold outcome.", tone: "neutral" },
  technical: { label: "Technical", description: "Retrying after a failure of ours or the carrier's.", tone: "warning" },
  blocked: { label: "Blocked", description: "Do not call, or no consent evidence.", tone: "danger" },
  needs_disposition: { label: "Needs outcome", description: "A rep took the call and has not said what happened.", tone: "warning" },
  exhausted: { label: "Finished", description: "Out of attempts, or past the cadence's end.", tone: "neutral" },
  double_membership: { label: "On two lists", description: "Dialable on this campaign and another: two lists could call one person.", tone: "danger" },
};

export function bucketText(bucket: string): Term & { tone: Tone } {
  return BUCKET[bucket as Bucket] ?? { label: bucket, description: "", tone: "outline" };
}

// --- spans -----------------------------------------------------------------------------------------

/** "4 h", "90 s", "3 d", "1 h 30 min", "right away": how a wait reads on the page. */
export function spanText(seconds: number): string {
  if (seconds <= 0) return "right away";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 48) return rest ? `${hours} h ${rest} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const hoursLeft = hours % 24;
  return hoursLeft ? `${days} d ${hoursLeft} h` : `${days} d`;
}

/** "in 3 h 58 min", "2 min ago". */
export function relativeSpan(iso: string, now: number = Date.now()): string {
  const delta = Math.round((new Date(iso).getTime() - now) / 1000);
  if (Math.abs(delta) < 45) return delta >= 0 ? "now" : "just now";
  return delta > 0 ? `in ${spanText(delta)}` : `${spanText(-delta)} ago`;
}

/** One line for a lead's place on a cadence campaign: when, why, and where in the cycle. */
export function cadenceNextLine(m: LeadCadenceMembership, now: number = Date.now()): string {
  const where = m.cadence ? `${positionText(m.cycle, m.step)}` : null;
  const why = nextReasonText(m.next_reason);
  if (m.held_by) {
    const until = m.next_at ? ` until ${formatDateTime(m.next_at)}` : "";
    const reason = m.held_by === "cadence:paused" ? "paused" : m.held_by.replaceAll("_", " ");
    return [`Held${until} (${reason})`, where].filter(Boolean).join(" · ");
  }
  if (m.state === "exhausted" || m.state === "done" || m.state === "dnc" || m.state === "retired" || m.state === "blocked") {
    return [why ?? m.state, where].filter(Boolean).join(" · ");
  }
  if (m.state === "offered" || m.state === "in_flight" || m.state === "needs_disposition") {
    return ["On a call", where].filter(Boolean).join(" · ");
  }
  const when = m.next_attempt_at ? `Due ${relativeSpan(m.next_attempt_at, now)}` : "Due now";
  return [when, why, where].filter(Boolean).join(" · ");
}

/** A cycle in a phrase: "2 calls 1 min apart, then 4 h"; "3 calls back to back, then 4 h". */
export function cycleText(phase: CadencePhase): string {
  const c = phase.cycle;
  const calls = c.attempts === 1 ? "1 call" : `${c.attempts} calls`;
  const first = c.between_attempts_seconds?.[0] ?? 0;
  const between =
    c.attempts > 1 && c.between_attempts_seconds?.length
      ? first <= 0 ? " back to back" : ` ${spanText(first)} apart`
      : "";
  return `${calls}${between}, then ${spanText(c.after_cycle_seconds)}`;
}

/** The body in one line for a list: each phase's cycle and when it ends. */
export function bodySummary(body: CadenceBody): string {
  return body.phases
    .map((p) => {
      const ends = p.ends_days_after_enrollment ? `to day ${p.ends_days_after_enrollment}` : "open-ended";
      return `${p.name}: ${cycleText(p)} (${ends})`;
    })
    .join(" · ");
}

// --- the editor ------------------------------------------------------------------------------------

/** The bounds `cadence_body.py` enforces, in the units the form asks for. */
export const CADENCE_BOUNDS = {
  wait_minutes: [1, 30 * 24 * 60],
  /** Between two calls of one cycle: 0 is right away (ADR-069). */
  between_minutes: [0, 30 * 24 * 60],
  initial_delay_minutes: [0, 7 * 24 * 60],
  attempts: [1, 20],
  total_attempts: [1, 100],
  expire_days: [1, 365],
  /** Calls to one number in a day (ADR-067); the same ceiling as a cycle's calls. */
  daily_calls_per_number: [1, 20],
  priority: [0, 999],
  decay: [0, 100],
  technical_retry_minutes: [1, 24 * 60],
  max_retries: [0, 10],
  invalid_stops_after: [1, 10],
  not_worked_minutes: [1, 24 * 60],
  phases: [1, 6],
} as const;

export const CADENCE_SLUG_RE = /^[a-z][a-z0-9_]{1,79}$/;
export const PHASE_NAME_RE = /^[a-z][a-z0-9_]{0,23}$/;

export interface PhaseValues {
  name: string;
  /** "" for an open-ended last phase. */
  ends_days: string;
  priority_band: number;
  age_decay_per_day: number;
  /** "" for never. */
  not_after_minutes: string;
  attempts: number;
  between_minutes: number[];
  after_cycle_minutes: number;
  initial_delay_minutes: number;
  did_strategy: string[];
}

export interface CadenceFormValues {
  name: string;
  slug: string;
  note: string;
  phases: PhaseValues[];
  total_attempts: number;
  /** "" for never. */
  expire_days: string;
  /** At most this many calls to one number a day, from any campaign, a hand dial too; "" for no cap. */
  daily_calls_per_number: string;
  technical_retry_minutes: number;
  max_retries: number;
  invalid_stops_after: number;
  not_worked_minutes: number;
  abandoned_counts: boolean;
  manual_dial_counts: boolean;
  /** A rep's outcome on a call the lead made spends an attempt (ADR-071). */
  inbound_counts: boolean;
  callback_default_minutes: number;
  priority_callback: number;
  priority_about_to_sign: number;
  priority_mid_cycle: number;
  priority_initial_fresh: number;
}

/** The client's default: two calls a minute apart, four hours between cycles, one call every
 *  three days after two weeks, never more than three calls to a number in a day (the meeting's
 *  numbers, taken as the default on 2026-09-25). */
export const NEW_CADENCE: CadenceFormValues = {
  name: "",
  slug: "",
  note: "",
  phases: [
    {
      name: "initial", ends_days: "14", priority_band: 400, age_decay_per_day: 10, not_after_minutes: "120",
      attempts: 2, between_minutes: [1], after_cycle_minutes: 240, initial_delay_minutes: 0,
      did_strategy: ["branded", "same_as_last"],
    },
    {
      name: "followup", ends_days: "90", priority_band: 100, age_decay_per_day: 0, not_after_minutes: "1440",
      attempts: 1, between_minutes: [], after_cycle_minutes: 3 * 24 * 60, initial_delay_minutes: 0,
      did_strategy: ["branded"],
    },
  ],
  total_attempts: 40,
  expire_days: "90",
  daily_calls_per_number: "3",
  technical_retry_minutes: 5,
  max_retries: 2,
  invalid_stops_after: 2,
  not_worked_minutes: 10,
  abandoned_counts: true,
  manual_dial_counts: true,
  inbound_counts: true,
  callback_default_minutes: 60,
  priority_callback: 900,
  priority_about_to_sign: 800,
  priority_mid_cycle: 700,
  priority_initial_fresh: 500,
};

const minutes = (seconds: number | null | undefined, fallback: number) =>
  seconds === null || seconds === undefined ? fallback : Math.round(seconds / 60);

/** The form for a saved body. Fields the form does not edit are carried by `bodyFromForm`. */
export function formFromBody(body: CadenceBody, name = "", slug = ""): CadenceFormValues {
  return {
    name,
    slug,
    note: "",
    phases: body.phases.map((p) => ({
      name: p.name,
      ends_days: p.ends_days_after_enrollment === null || p.ends_days_after_enrollment === undefined ? "" : String(p.ends_days_after_enrollment),
      priority_band: p.priority_band,
      age_decay_per_day: p.age_decay_per_day ?? 0,
      not_after_minutes: p.not_after_seconds === null || p.not_after_seconds === undefined ? "" : String(minutes(p.not_after_seconds, 0)),
      attempts: p.cycle.attempts,
      between_minutes: (p.cycle.between_attempts_seconds ?? []).map((s) => minutes(s, 1)),
      after_cycle_minutes: minutes(p.cycle.after_cycle_seconds, 240),
      initial_delay_minutes: minutes(p.cycle.initial_delay_seconds ?? 0, 0),
      did_strategy: p.cycle.did_strategy?.length ? [...p.cycle.did_strategy] : ["branded"],
    })),
    total_attempts: body.limits?.total_attempts ?? 40,
    expire_days: body.limits?.expire_days_after_enrollment === null || body.limits?.expire_days_after_enrollment === undefined ? "" : String(body.limits.expire_days_after_enrollment),
    daily_calls_per_number: body.limits?.daily_calls_per_number === null || body.limits?.daily_calls_per_number === undefined ? "" : String(body.limits.daily_calls_per_number),
    technical_retry_minutes: minutes(body.technical?.retry_seconds, 5),
    max_retries: body.technical?.max_retries ?? 2,
    invalid_stops_after: body.technical?.invalid_stops_after ?? 2,
    not_worked_minutes: minutes(body.on_outcome?.not_worked_seconds, 10),
    abandoned_counts: body.on_outcome?.abandoned_counts ?? true,
    manual_dial_counts: body.on_outcome?.manual_dial_counts ?? true,
    inbound_counts: body.on_outcome?.inbound_counts ?? true,
    callback_default_minutes: minutes(body.on_outcome?.callback_default_seconds, 60),
    priority_callback: body.priority?.callback ?? 900,
    priority_about_to_sign: body.priority?.about_to_sign ?? 800,
    priority_mid_cycle: body.priority?.mid_cycle ?? 700,
    priority_initial_fresh: body.priority?.initial_fresh ?? 500,
  };
}

/**
 * The body core-api validates, from the form. `previous` is the body being edited: what the form
 * does not show (the opening block, the technical cause lists, the fields this build does not
 * run) rides along unchanged, so an edit of a wait never drops an opening somebody typed as JSON.
 */
export function bodyFromForm(v: CadenceFormValues, previous?: CadenceBody | null): CadenceBody {
  const phases: CadencePhase[] = v.phases.map((p, i) => {
    const kept = previous?.phases?.[i];
    return {
      name: p.name.trim(),
      ends_days_after_enrollment: p.ends_days.trim() === "" ? null : Number(p.ends_days),
      priority_band: p.priority_band,
      age_decay_per_day: p.age_decay_per_day,
      ...(kept?.priority_floor !== undefined && kept?.priority_floor !== null ? { priority_floor: kept.priority_floor } : {}),
      not_after_seconds: p.not_after_minutes.trim() === "" ? null : Number(p.not_after_minutes) * 60,
      cycle: {
        attempts: p.attempts,
        between_attempts_seconds: p.between_minutes.slice(0, Math.max(0, p.attempts - 1)).map((m) => m * 60),
        after_cycle_seconds: p.after_cycle_minutes * 60,
        initial_delay_seconds: p.initial_delay_minutes * 60,
        did_strategy: p.did_strategy.slice(0, p.attempts),
        ...(kept?.cycle?.voicemail_after_attempt !== undefined && kept?.cycle?.voicemail_after_attempt !== null
          ? { voicemail_after_attempt: kept.cycle.voicemail_after_attempt }
          : {}),
      },
    };
  });
  return {
    clock: "host",
    phases,
    ...(previous?.opening ? { opening: previous.opening } : {}),
    limits: {
      total_attempts: v.total_attempts,
      expire_days_after_enrollment: v.expire_days.trim() === "" ? null : Number(v.expire_days),
      daily_calls_per_number: v.daily_calls_per_number.trim() === "" ? null : Number(v.daily_calls_per_number),
    },
    technical: {
      ...(previous?.technical ?? {}),
      retry_seconds: v.technical_retry_minutes * 60,
      max_retries: v.max_retries,
      invalid_stops_after: v.invalid_stops_after,
    },
    on_outcome: {
      ...(previous?.on_outcome ?? {}),
      not_worked_seconds: v.not_worked_minutes * 60,
      abandoned_counts: v.abandoned_counts,
      manual_dial_counts: v.manual_dial_counts,
      inbound_counts: v.inbound_counts,
      callback_default_seconds: v.callback_default_minutes * 60,
    },
    priority: {
      ...(previous?.priority ?? {}),
      callback: v.priority_callback,
      about_to_sign: v.priority_about_to_sign,
      mid_cycle: v.priority_mid_cycle,
      initial_fresh: v.priority_initial_fresh,
    },
  };
}

const wholeIn = (n: unknown, [min, max]: readonly [number, number]) =>
  typeof n === "number" && Number.isInteger(n) && n >= min && n <= max;

/**
 * What the server would refuse, said on the field first. Keys are dotted paths into the form
 * (`phases.0.attempts`); an empty object is a form the server will take.
 */
export function formProblems(v: CadenceFormValues): Record<string, string> {
  const out: Record<string, string> = {};
  const b = CADENCE_BOUNDS;
  if (!v.name.trim()) out.name = "Give the cadence a name";
  if (!CADENCE_SLUG_RE.test(v.slug)) out.slug = "Lowercase letters, digits and underscores, 2 to 80 long, starting with a letter";
  if (v.phases.length < b.phases[0] || v.phases.length > b.phases[1]) out.phases = `Between ${b.phases[0]} and ${b.phases[1]} phases`;
  let previous = 0;
  const names = new Set<string>();
  v.phases.forEach((p, i) => {
    const at = (field: string) => `phases.${i}.${field}`;
    if (!PHASE_NAME_RE.test(p.name.trim())) out[at("name")] = "Lowercase letters, digits and underscores, up to 24";
    if (names.has(p.name.trim())) out[at("name")] = "Phase names are unique";
    names.add(p.name.trim());
    const last = i === v.phases.length - 1;
    if (p.ends_days.trim() === "") {
      if (!last) out[at("ends_days")] = "Only the last phase may be open-ended";
    } else {
      const days = Number(p.ends_days);
      if (!wholeIn(days, b.expire_days)) out[at("ends_days")] = `A whole number of days from ${b.expire_days[0]} to ${b.expire_days[1]}`;
      else if (days <= previous) out[at("ends_days")] = "Phases end in increasing order";
      previous = days;
    }
    if (!wholeIn(p.priority_band, b.priority)) out[at("priority_band")] = `From ${b.priority[0]} to ${b.priority[1]}`;
    if (!wholeIn(p.age_decay_per_day, b.decay)) out[at("age_decay_per_day")] = `From ${b.decay[0]} to ${b.decay[1]} a day`;
    if (p.not_after_minutes.trim() !== "" && !wholeIn(Number(p.not_after_minutes), b.wait_minutes)) {
      out[at("not_after_minutes")] = `A whole number of minutes from ${b.wait_minutes[0]} to ${b.wait_minutes[1].toLocaleString()}, or empty for never`;
    }
    if (!wholeIn(p.attempts, b.attempts)) out[at("attempts")] = `From ${b.attempts[0]} to ${b.attempts[1]} calls`;
    if (p.attempts > 1 && p.between_minutes.length === 0) out[at("between_minutes")] = "A cycle of more than one call needs a wait between the calls";
    if (p.between_minutes.length > Math.max(0, p.attempts - 1)) out[at("between_minutes")] = `At most ${p.attempts - 1} wait${p.attempts - 1 === 1 ? "" : "s"} between ${p.attempts} calls`;
    if (p.between_minutes.some((m) => !wholeIn(m, b.between_minutes))) out[at("between_minutes")] = `Each wait is right away or up to ${b.between_minutes[1].toLocaleString()} minutes`;
    if (!wholeIn(p.after_cycle_minutes, b.wait_minutes)) out[at("after_cycle_minutes")] = `From ${b.wait_minutes[0]} to ${b.wait_minutes[1].toLocaleString()} minutes`;
    if (!wholeIn(p.initial_delay_minutes, b.initial_delay_minutes)) out[at("initial_delay_minutes")] = `From 0 to ${b.initial_delay_minutes[1].toLocaleString()} minutes`;
    if (p.did_strategy.length === 0 || p.did_strategy.length > p.attempts) out[at("did_strategy")] = "One strategy per call of the cycle, at most";
  });
  if (!wholeIn(v.total_attempts, b.total_attempts)) out.total_attempts = `From ${b.total_attempts[0]} to ${b.total_attempts[1]} attempts`;
  if (v.expire_days.trim() !== "" && !wholeIn(Number(v.expire_days), b.expire_days)) out.expire_days = `A whole number of days from ${b.expire_days[0]} to ${b.expire_days[1]}, or empty for never`;
  if (v.daily_calls_per_number.trim() !== "" && !wholeIn(Number(v.daily_calls_per_number), b.daily_calls_per_number)) out.daily_calls_per_number = `A whole number from ${b.daily_calls_per_number[0]} to ${b.daily_calls_per_number[1]} calls to a number in a day, or empty for no cap`;
  if (!wholeIn(v.technical_retry_minutes, b.technical_retry_minutes)) out.technical_retry_minutes = `From ${b.technical_retry_minutes[0]} to ${b.technical_retry_minutes[1]} minutes`;
  if (!wholeIn(v.max_retries, b.max_retries)) out.max_retries = `From ${b.max_retries[0]} to ${b.max_retries[1]}`;
  if (!wholeIn(v.invalid_stops_after, b.invalid_stops_after)) out.invalid_stops_after = `From ${b.invalid_stops_after[0]} to ${b.invalid_stops_after[1]}`;
  if (!wholeIn(v.not_worked_minutes, b.not_worked_minutes)) out.not_worked_minutes = `From ${b.not_worked_minutes[0]} to ${b.not_worked_minutes[1]} minutes`;
  if (!wholeIn(v.callback_default_minutes, b.wait_minutes)) out.callback_default_minutes = `From ${b.wait_minutes[0]} to ${b.wait_minutes[1].toLocaleString()} minutes`;
  for (const key of ["priority_callback", "priority_about_to_sign", "priority_mid_cycle", "priority_initial_fresh"] as const) {
    if (!wholeIn(v[key], b.priority)) out[key] = `From ${b.priority[0]} to ${b.priority[1]}`;
  }
  return out;
}

/** The waits list resized to a cycle's calls: one fewer than the calls, the last wait repeated. */
export function waitsFor(attempts: number, waits: number[]): number[] {
  const wanted = Math.max(0, attempts - 1);
  if (waits.length >= wanted) return waits.slice(0, wanted);
  const last = waits[waits.length - 1] ?? 1;
  return [...waits, ...Array.from({ length: wanted - waits.length }, () => last)];
}

// --- the flow editor's arithmetic ------------------------------------------------------------------

/** The units a wait is shown in. The form keeps minutes; these only choose how a chip reads. */
export const DURATION_UNITS = ["min", "h", "d"] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];
export const MINUTES_PER: Record<DurationUnit, number> = { min: 1, h: 60, d: 24 * 60 };

/** The largest unit that divides the minutes evenly: 240 → 4 h, 4320 → 3 d, 90 → 90 min. */
export function durationParts(minutes: number): { value: number; unit: DurationUnit } {
  if (!Number.isFinite(minutes)) return { value: Number.NaN, unit: "min" };
  if (minutes <= 0) return { value: minutes, unit: "min" };
  for (const unit of ["d", "h"] as const) {
    if (minutes % MINUTES_PER[unit] === 0) return { value: minutes / MINUTES_PER[unit], unit };
  }
  return { value: minutes, unit: "min" };
}

/** The strategy list resized to a cycle's calls, the last strategy repeated. */
export function strategiesFor(attempts: number, strategies: string[]): string[] {
  const wanted = Math.max(1, attempts);
  if (strategies.length >= wanted) return strategies.slice(0, wanted);
  const last = strategies[strategies.length - 1] ?? "branded";
  return [...strategies, ...Array.from({ length: wanted - strategies.length }, () => last)];
}

/** One more call at the end of the cycle: it waits as long as the last gap did and uses the same
 *  number as the call before it. */
export function withCall(phase: PhaseValues): PhaseValues {
  const attempts = phase.attempts + 1;
  const strategies = strategiesFor(phase.attempts, phase.did_strategy);
  return {
    ...phase,
    attempts,
    between_minutes: waitsFor(attempts, phase.between_minutes),
    did_strategy: [...strategies, phase.attempts >= 1 ? "same_as_last" : "branded"],
  };
}

/** The cycle without its call `index`; the gap before it goes with it (the first call keeps no
 *  gap, so removing it drops the gap after it). A cycle keeps at least one call. */
export function withoutCall(phase: PhaseValues, index: number): PhaseValues {
  if (phase.attempts <= 1) return phase;
  const waits = waitsFor(phase.attempts, phase.between_minutes);
  const strategies = strategiesFor(phase.attempts, phase.did_strategy);
  const gap = Math.max(0, index - 1);
  return {
    ...phase,
    attempts: phase.attempts - 1,
    between_minutes: waits.filter((_, i) => i !== gap),
    did_strategy: strategies.filter((_, i) => i !== index),
  };
}

/** The problems of one phase, for the list under its card. */
export function phaseProblems(problems: Record<string, string>, index: number): string[] {
  const prefix = `phases.${index}.`;
  return Object.entries(problems)
    .filter(([key]) => key.startsWith(prefix))
    .map(([, message]) => message);
}
